import React, { memo } from 'react';
import styled from 'styled-components';

import { Colors } from 'context';

/**
 * Fullscreen toggle for the whiteboard (spec 0010).
 *
 * Rendered through excalidraw's `renderTopRightUI` slot rather than as our own
 * absolutely-positioned overlay: the slot sits inside
 * `layer-ui__wrapper__top-right`, so excalidraw lays our button out next to its
 * own controls instead of on top of them. It also renders in BOTH view and edit
 * mode (the Library button next to it is the one gated by `viewModeEnabled`),
 * which is exactly what we need — teacher and student both get the button.
 *
 * The icon is inline SVG on purpose: the repo's Icon atom has no
 * expand/collapse glyph, and `@ant-design/icons` is only a transitive dep —
 * importing it trips eslint `import/no-extraneous-dependencies` and fails the
 * build.
 */

interface IFullscreenButton {
  isFullscreen: boolean;
  onToggle: () => void;
  label: string;
}

export const FullscreenButton = memo(({ isFullscreen, onToggle, label }: IFullscreenButton) => (
  <Button type="button" onClick={onToggle} title={label} aria-label={label}>
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      {isFullscreen ? (
        <>
          <polyline points="4 14 10 14 10 20" />
          <polyline points="20 10 14 10 14 4" />
          <line x1="14" y1="10" x2="21" y2="3" />
          <line x1="3" y1="21" x2="10" y2="14" />
        </>
      ) : (
        <>
          <polyline points="15 3 21 3 21 9" />
          <polyline points="9 21 3 21 3 15" />
          <line x1="21" y1="3" x2="14" y2="10" />
          <line x1="3" y1="21" x2="10" y2="14" />
        </>
      )}
    </svg>
  </Button>
));

/* Sized/coloured off excalidraw's own CSS vars so the button reads as part of
   its toolbar in both light and dark themes. */
const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--default-button-size, 2.25rem);
  height: var(--default-button-size, 2.25rem);
  padding: 0;
  cursor: pointer;
  color: var(--icon-fill-color, ${Colors.black});
  background: var(--island-bg-color, ${Colors.white});
  border: 1px solid var(--default-border-color, ${Colors.system_gray});
  border-radius: var(--border-radius-lg, 0.5rem);
  box-shadow: var(--shadow-island, 0 2px 6px rgba(0, 0, 0, 0.16));

  &:hover {
    background: var(--button-hover-bg, ${Colors.bg_gray});
  }
`;
